import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const TOKEN_KEY = "planflow.auth_token";
const USER_KEY = "planflow.auth_user";

export const AuthContext = createContext({
    user: null,
    loading: true,
    login: async () => {},
    register: async () => {},
    loginWithGoogleCredential: async () => {},
    updateProfilePicture: async () => {},
    updatePreferences: async () => {},
    logout: () => {},
});

export function useAuth() {
    return useContext(AuthContext);
}

function readStored() {
    try {
        const token = localStorage.getItem(TOKEN_KEY);
        const userRaw = localStorage.getItem(USER_KEY);
        if (!token) return { token: null, user: null };
        return { token, user: userRaw ? JSON.parse(userRaw) : null };
    } catch {
        return { token: null, user: null };
    }
}

function persist(token, user) {
    try {
        if (token) localStorage.setItem(TOKEN_KEY, token);
        if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch {
        /* noop */
    }
}

function clearStored() {
    try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
    } catch {
        /* noop */
    }
}

// Axios interceptor: attach bearer token to every request.
api.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// On 401, clear session so the app redirects to login.
api.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err?.response?.status === 401) {
            clearStored();
        }
        return Promise.reject(err);
    },
);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const { token, user: cachedUser } = readStored();
        const ipLogin = () =>
            api.post("/auth/ip-login")
                .then(({ data }) => {
                    persist(data.access_token, data.user);
                    setUser(data.user);
                })
                .catch(() => setUser(null));
        if (!token) {
            ipLogin().finally(() => setLoading(false));
            return;
        }
        // Optimistically show cached user, then verify with server.
        if (cachedUser) setUser(cachedUser);
        api.get("/auth/me")
            .then(({ data }) => {
                setUser(data);
                persist(token, data);
            })
            .catch(() => {
                clearStored();
                return ipLogin();
            })
            .finally(() => setLoading(false));
    }, []);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        persist(data.access_token, data.user);
        setUser(data.user);
        return data.user;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        persist(data.access_token, data.user);
        setUser(data.user);
        return data.user;
    };

    const loginWithGoogleCredential = async (credential) => {
        // 30s timeout so a pegged backend surfaces an error instead of
        // hanging the "Finalizing Google sign-in…" overlay forever.
        const { data } = await api.post(
            "/auth/google",
            { credential },
            { timeout: 30000 },
        );
        persist(data.access_token, data.user);
        setUser(data.user);
        return data.user;
    };

    const updateProfilePicture = async (dataUrl) => {
        const { data } = await api.post("/auth/profile-picture", {
            picture: dataUrl || "",
        });
        const next = { ...(user || {}), picture: data.picture };
        setUser(next);
        persist(localStorage.getItem(TOKEN_KEY), next);
        return data.picture;
    };

    // iter-116: per-user preferences (theme, future settings). Patches
    // the user doc server-side and mirrors the response into local
    // state + localStorage so ThemeApplier reacts instantly.
    const updatePreferences = async (patch) => {
        const { data } = await api.patch("/auth/preferences", patch || {});
        setUser(data);
        persist(localStorage.getItem(TOKEN_KEY), data);
        return data;
    };

    // iter-169i: Company profile (installer/designer sub-docs) +
    // saved applicant signature for auto-fill/auto-stamp on schedules.
    const updateCompanyProfile = async (patch) => {
        const { data } = await api.patch("/auth/company-profile", patch || {});
        setUser(data);
        persist(localStorage.getItem(TOKEN_KEY), data);
        return data;
    };

    const logout = () => {
        clearStored();
        setUser(null);
    };

    return (
        <AuthContext.Provider
            value={{ user, loading, login, register, loginWithGoogleCredential, updateProfilePicture, updatePreferences, updateCompanyProfile, logout }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function formatApiError(err) {
    const detail = err?.response?.data?.detail;
    if (!detail) return err?.message || "Something went wrong";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" · ");
    }
    return String(detail);
}
